/**
 * @file Filterable page list (status, chapter, title search) with last modified columns.
 */
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DragHandleIcon from "@mui/icons-material/DragHandle";
import {
  Box,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { Link, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { apiFetch } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { canManageContent, isAdmin } from "@/lib/permissions";
import { formatDateTime } from "@/lib/format-date";
import { PageStatusBadge } from "@/components/PageStatusBadge";
import { PAGE_FILTER_SENTINEL_LABELS, PAGE_STATUS_LABELS } from "@/lib/page-status-labels";
import type { PageStatus } from "@/lib/shared";
import { pageDetailHref } from "@/lib/pages-navigation";
import { LastModifiedBy, type LastModifierIdentity } from "./LastModifiedBy";
import type { ReactNode } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

/** Sentinels for API filters (see GET /pages?reviewQueue=1 and ?publishedLive=1). */
export const STATUS_REVIEW_QUEUE = "__review_queue__";
export const STATUS_PUBLISHED_LIVE = "__published_live__";

const STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: "", label: "All statuses" },
  { value: STATUS_REVIEW_QUEUE, label: PAGE_FILTER_SENTINEL_LABELS.reviewQueue },
  { value: STATUS_PUBLISHED_LIVE, label: PAGE_FILTER_SENTINEL_LABELS.publishedLive },
  ...(
    [
      "draft",
      "in_review",
      "approved",
      "published",
      "published_draft",
      "published_in_review",
      "unpublished",
      "rejected",
    ] as PageStatus[]
  ).map((s) => ({ value: s, label: PAGE_STATUS_LABELS[s] })),
];

export interface PageListApiRow {
  id: string;
  title: string;
  slug: string;
  /** Order within the chapter (0-based). */
  sortOrder: number;
  status: string;
  updatedAt: string;
  chapter: {
    id: string;
    title: string;
    slug: string;
    guide: { id: string; title: string; slug: string };
  };
  createdBy: string;
  currentVersion: {
    id: string;
    createdBy: string;
  } | null;
}

interface ChapterOption {
  id: string;
  title: string;
}

function lastModifier(row: PageListApiRow): LastModifierIdentity {
  return { createdBy: row.currentVersion?.createdBy ?? row.createdBy };
}

const LIVE_PAGE_STATUSES = new Set(["published", "published_draft", "published_in_review"]);

function PageRowDeleteButton({
  row,
  canDelete,
  onRequestDelete,
}: {
  row: PageListApiRow;
  canDelete: boolean;
  onRequestDelete: (row: PageListApiRow) => void;
}) {
  if (!canDelete) {
    return null;
  }
  return (
    <TableCell align="right">
      <Tooltip title="Delete page">
        <IconButton
          size="small"
          color="error"
          onClick={() => onRequestDelete(row)}
          aria-label={`Delete page ${row.title}`}
        >
          <DeleteOutlineIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </TableCell>
  );
}

function SortablePageRow({
  row,
  listChapterId,
  canDelete,
  onRequestDelete,
}: {
  row: PageListApiRow;
  listChapterId: string;
  canDelete: boolean;
  onRequestDelete: (row: PageListApiRow) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: row.id,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.55 : undefined,
  };
  const ord = (row.sortOrder ?? 0) + 1;

  return (
    <TableRow ref={setNodeRef} style={style} hover>
      <TableCell sx={{ width: 48, pr: 0, verticalAlign: "middle" }}>
        <Tooltip title="Drag to reorder">
          <IconButton
            size="small"
            {...attributes}
            {...listeners}
            sx={{ cursor: "grab", touchAction: "none" }}
            aria-label="Drag to reorder page"
          >
            <DragHandleIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </TableCell>
      <TableCell>
        <Link
          to={pageDetailHref(row.id, listChapterId)}
          style={{ fontWeight: 600, textDecoration: "none" }}
        >
          {row.title}
        </Link>
        <Typography variant="caption" display="block" color="text.secondary">
          {row.slug}
        </Typography>
      </TableCell>
      <TableCell>
        {row.chapter.guide.title} › {row.chapter.title}
      </TableCell>
      <TableCell>
        <PageStatusBadge status={row.status} />
      </TableCell>
      <TableCell>{ord}</TableCell>
      <TableCell sx={{ whiteSpace: "nowrap" }}>{formatDateTime(row.updatedAt)}</TableCell>
      <TableCell>
        <LastModifiedBy user={lastModifier(row)} />
      </TableCell>
      <PageRowDeleteButton row={row} canDelete={canDelete} onRequestDelete={onRequestDelete} />
    </TableRow>
  );
}

export interface PagesDirectoryProps {
  title: string;
  description?: string;
  /** When true, only pages in the approval queue (in review or published+in review). */
  reviewQueueOnly?: boolean;
  /** When false, title and description are not rendered (use an outer page header). */
  showHeading?: boolean;
  /** Shown on the right of the title row (e.g. New page). */
  actions?: ReactNode;
}

/**
 * Full pages table with filters (used on All pages and Approvals).
 */
export function PagesDirectory({
  title,
  description,
  reviewQueueOnly,
  showHeading = true,
  actions,
}: PagesDirectoryProps) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const canEdit = useMemo(() => canManageContent(user?.roles), [user?.roles]);
  const canDelete = useMemo(() => isAdmin(user?.roles), [user?.roles]);

  const [chapters, setChapters] = useState<ChapterOption[]>([]);
  const [pageToDelete, setPageToDelete] = useState<PageListApiRow | null>(null);
  const [rows, setRows] = useState<PageListApiRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reorderPending, setReorderPending] = useState(false);
  const requestSeqRef = useRef(0);

  const [status, setStatus] = useState("");
  const [qInput, setQInput] = useState("");
  const [q, setQ] = useState("");

  /** Chapter filter — synced with `?chapterId=` (e.g. deep link from Chapters). */
  const chapterId = searchParams.get("chapterId") ?? "";

  const showReorder = Boolean(chapterId) && canEdit;
  const colCount = 6 + (showReorder ? 1 : 0) + (canDelete ? 1 : 0);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  useEffect(() => {
    const t = setTimeout(() => setQ(qInput), 400);
    return () => clearTimeout(t);
  }, [qInput]);

  useEffect(() => {
    void apiFetch<Array<{ id: string; title: string; guide: { title: string } }>>("/api/v1/chapters")
      .then((list) =>
        setChapters(
          list.map((c) => ({
            id: c.id,
            title: c.guide ? `${c.guide.title} › ${c.title}` : c.title,
          })),
        ),
      )
      .catch(() => undefined);
  }, []);

  /** Deep link from overview (and keep URL in sync with status filter on All pages). */
  useEffect(() => {
    if (reviewQueueOnly) {
      return;
    }
    const rq = searchParams.get("reviewQueue");
    const pl = searchParams.get("publishedLive");
    const st = searchParams.get("status");
    if (rq === "1") {
      setStatus(STATUS_REVIEW_QUEUE);
    } else if (pl === "1") {
      setStatus(STATUS_PUBLISHED_LIVE);
    } else if (st && STATUS_OPTIONS.some((o) => o.value === st)) {
      setStatus(st);
    } else {
      setStatus("");
    }
  }, [reviewQueueOnly, searchParams]);

  const load = useCallback(async () => {
    const requestId = ++requestSeqRef.current;
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (reviewQueueOnly) {
        params.set("reviewQueue", "1");
      } else if (status === STATUS_REVIEW_QUEUE) {
        params.set("reviewQueue", "1");
      } else if (status === STATUS_PUBLISHED_LIVE) {
        params.set("publishedLive", "1");
      } else if (status) {
        params.set("status", status);
      }
      if (chapterId) {
        params.set("chapterId", chapterId);
      }
      if (q.trim()) {
        params.set("q", q.trim());
      }
      const qs = params.toString();
      const data = await apiFetch<PageListApiRow[]>(`/api/v1/pages${qs ? `?${qs}` : ""}`);
      if (requestId !== requestSeqRef.current) {
        return;
      }
      setRows(data);
    } catch (e) {
      if (requestId !== requestSeqRef.current) {
        return;
      }
      setError(e instanceof Error ? e.message : "Failed to load pages");
    } finally {
      if (requestId === requestSeqRef.current) {
        setLoading(false);
      }
    }
  }, [reviewQueueOnly, status, chapterId, q]);

  function applyStatusToUrl(nextStatus: string) {
    if (reviewQueueOnly || pathname !== "/dashboard/pages") {
      return;
    }
    const params = new URLSearchParams(searchParams.toString());
    params.delete("reviewQueue");
    params.delete("publishedLive");
    params.delete("status");
    if (nextStatus === STATUS_REVIEW_QUEUE) {
      params.set("reviewQueue", "1");
    } else if (nextStatus === STATUS_PUBLISHED_LIVE) {
      params.set("publishedLive", "1");
    } else if (nextStatus) {
      params.set("status", nextStatus);
    }
    const qs = params.toString();
    navigate(qs ? `${pathname}?${qs}` : pathname, { replace: true });
  }

  function applyChapterToUrl(nextChapterId: string) {
    const params = new URLSearchParams(searchParams.toString());
    if (nextChapterId) {
      params.set("chapterId", nextChapterId);
    } else {
      params.delete("chapterId");
    }
    const qs = params.toString();
    navigate(qs ? `${pathname}?${qs}` : pathname, { replace: true });
  }

  useEffect(() => {
    void load();
  }, [load]);

  async function handleDragEnd(event: DragEndEvent) {
    if (!chapterId) {
      return;
    }
    const { active, over } = event;
    if (!over || active.id === over.id) {
      return;
    }
    const oldIndex = rows.findIndex((r) => r.id === active.id);
    const newIndex = rows.findIndex((r) => r.id === over.id);
    if (oldIndex < 0 || newIndex < 0) {
      return;
    }
    const reordered = arrayMove(rows, oldIndex, newIndex);
    setRows(reordered);
    setReorderPending(true);
    setError(null);
    try {
      const updated = await apiFetch<PageListApiRow[]>("/api/v1/pages/reorder", {
        method: "POST",
        json: { chapterId, pageIds: reordered.map((r) => r.id) },
      });
      setRows(updated);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      await load();
    } finally {
      setReorderPending(false);
    }
  }

  const sortableIds = rows.map((r) => r.id);

  function requestDeletePage(p: PageListApiRow) {
    setPageToDelete(p);
  }

  async function confirmDeletePage() {
    if (!pageToDelete) {
      return;
    }
    const p = pageToDelete;
    setPageToDelete(null);
    setError(null);
    try {
      await apiFetch(`/api/v1/pages/${p.id}`, { method: "DELETE" });
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  const deletePageMessage = pageToDelete
    ? LIVE_PAGE_STATUSES.has(pageToDelete.status)
      ? `Delete page “${pageToDelete.title}”? It is or was live on the help site. It will be removed from the CMS and help site.`
      : `Delete page “${pageToDelete.title}”? It will be removed from the CMS and help site.`
    : "";

  const tableContent = (
    <Table size="medium">
      <TableHead>
        <TableRow sx={{ bgcolor: "action.hover" }}>
          {showReorder ? <TableCell width={48} aria-label="Reorder" /> : null}
          <TableCell>Title</TableCell>
          <TableCell>Chapter</TableCell>
          <TableCell>Status</TableCell>
          <TableCell width={88}>Order</TableCell>
          <TableCell>Last modified</TableCell>
          <TableCell>Last edited by</TableCell>
          {canDelete ? (
            <TableCell align="right">
              Actions
            </TableCell>
          ) : null}
        </TableRow>
      </TableHead>
      <TableBody>
        {loading ? (
          <TableRow>
            <TableCell colSpan={colCount}>
              <Typography variant="body2" color="text.secondary">
                Loading…
              </Typography>
            </TableCell>
          </TableRow>
        ) : rows.length === 0 ? (
          <TableRow>
            <TableCell colSpan={colCount}>
              <Typography variant="body2" color="text.secondary">
                No pages match the current filters.
              </Typography>
            </TableCell>
          </TableRow>
        ) : showReorder ? (
          <SortableContext items={sortableIds} strategy={verticalListSortingStrategy}>
            {rows.map((p) => (
              <SortablePageRow
                key={p.id}
                row={p}
                listChapterId={chapterId}
                canDelete={canDelete}
                onRequestDelete={requestDeletePage}
              />
            ))}
          </SortableContext>
        ) : (
          rows.map((p) => (
            <TableRow key={p.id} hover>
              <TableCell>
                <Link
                  to={pageDetailHref(p.id, chapterId)}
                  style={{ fontWeight: 600, textDecoration: "none" }}
                >
                  {p.title}
                </Link>
                <Typography variant="caption" display="block" color="text.secondary">
                  {p.slug}
                </Typography>
              </TableCell>
              <TableCell>
                {p.chapter.guide.title} › {p.chapter.title}
              </TableCell>
              <TableCell>
                <PageStatusBadge status={p.status} />
              </TableCell>
              <TableCell>{(p.sortOrder ?? 0) + 1}</TableCell>
              <TableCell sx={{ whiteSpace: "nowrap" }}>{formatDateTime(p.updatedAt)}</TableCell>
              <TableCell>
                <LastModifiedBy user={lastModifier(p)} />
              </TableCell>
              <PageRowDeleteButton row={p} canDelete={canDelete} onRequestDelete={requestDeletePage} />
            </TableRow>
          ))
        )}
      </TableBody>
    </Table>
  );

  return (
    <Box>
      {showHeading ? (
        <>
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
              mb: description ? 1 : 2,
            }}
          >
            <Typography variant="h4" component="h1" fontWeight={700}>
              {title}
            </Typography>
            {actions ? <Box sx={{ display: "flex", gap: 1 }}>{actions}</Box> : null}
          </Box>
          {description ? (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {description}
            </Typography>
          ) : null}
        </>
      ) : null}
      {error ? (
        <Typography color="error" variant="body2" sx={{ mb: 2 }}>
          {error}
        </Typography>
      ) : null}

      <Paper sx={{ p: 2, mb: 2, display: "flex", flexWrap: "wrap", gap: 2, alignItems: "center" }}>
        {!reviewQueueOnly ? (
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel id="filter-status">Status</InputLabel>
            <Select
              labelId="filter-status"
              label="Status"
              value={status}
              onChange={(e) => {
                const v = e.target.value;
                setStatus(v);
                applyStatusToUrl(v);
              }}
            >
              {STATUS_OPTIONS.map((o) => (
                <MenuItem key={o.value || "all"} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ) : null}

        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel id="filter-chapter">Chapter</InputLabel>
          <Select
            labelId="filter-chapter"
            label="Chapter"
            value={chapterId}
            onChange={(e) => applyChapterToUrl(e.target.value)}
          >
            <MenuItem value="">All chapters</MenuItem>
            {chapters.map((c) => (
              <MenuItem key={c.id} value={c.id}>
                {c.title}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          size="small"
          label="Filter by title or slug"
          value={qInput}
          onChange={(e) => setQInput(e.target.value)}
          sx={{ minWidth: 220, flex: "1 1 200px" }}
        />
      </Paper>

      {showReorder ? (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={(e) => void handleDragEnd(e)}>
          <TableContainer
            component={Paper}
            variant="outlined"
            sx={{
              opacity: reorderPending ? 0.7 : 1,
              transition: "opacity 0.2s",
            }}
          >
            {tableContent}
          </TableContainer>
        </DndContext>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          {tableContent}
        </TableContainer>
      )}

      <ConfirmDialog
        open={pageToDelete !== null}
        title="Delete page?"
        message={deletePageMessage}
        confirmLabel="Delete"
        danger
        onConfirm={() => void confirmDeletePage()}
        onClose={() => setPageToDelete(null)}
      />
    </Box>
  );
}
